import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Chat from './components/Chat';
import CenteredChatInput from './components/CenteredChatInput';
import WorkflowSidebar from './components/WorkflowSidebar';
import type { WorkflowSidebarHandle } from './components/WorkflowSidebar';
import ErrorBoundary from './components/ErrorBoundary';
import ChatSidebarWrapper from './components/ChatSidebarWrapper';
import LoadingSpinner from './components/LoadingSpinner';
import AppLayout from './components/AppLayout';
import WorkflowLibrary from './pages/WorkflowLibrary';
import ColumnMappingReview from './pages/ColumnMappingReview';
import KnowledgeBase from './pages/KnowledgeBase';
import { useSession } from './contexts/SessionContext';
import { FileUpload as FileUploadType, Script, ExecutionResult, ScriptBreakdown } from './services/api';
import { fileApi, scriptApi, sessionApi } from './services/api';
import ExecutionLoader from './components/ExecutionLoader';
import StyleGuide from './pages/StyleGuide';
import TestStreamingIssue from './components/TestStreamingIssue';

function AppContent() {
  const { currentSession, createNewSession, loadSession, generateTitle, updateSessionTitle, updateSessionMode } = useSession();
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadType[]>([]);
  const [currentScript, setCurrentScript] = useState<Script | null>(null);
  const [currentBreakdown, setCurrentBreakdown] = useState<ScriptBreakdown | null>(null);
  const [isAdaptedBreakdown, setIsAdaptedBreakdown] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [, setMessageCount] = useState(0);
  const [hasMessages, setHasMessages] = useState(false);
  const [workflowSidebarOpen, setWorkflowSidebarOpen] = useState(false);
  const [activeWorkflowTab, setActiveWorkflowTab] = useState('files');
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [showStyleGuide, setShowStyleGuide] = useState(false);
  const [showStreamingTest, setShowStreamingTest] = useState(false);
  const [currentPage, setCurrentPage] = useState<'launchpad' | 'workflow-library' | 'column-mappings' | 'knowledge-base'>('launchpad');
  const [pendingExecutionContext, setPendingExecutionContext] = useState<{executionId: string; sessionId: string} | null>(null);
  const [workflowContextId, setWorkflowContextId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState(1);
  
  // Ref for WorkflowSidebar to control streaming
  const workflowSidebarRef = useRef<WorkflowSidebarHandle>(null);
  
  // Debug state changes
  // Debug logging removed - state updates are working correctly

  // Session is now managed by SessionProvider, no need to create here

  // Keep the sidebar state as false initially to show the peek strip
  // The peek strip is visible when workflowSidebarOpen is false

  const handleMessageSent = useCallback(() => {
    setMessageCount(prev => {
      const newCount = prev + 1;
      // Generate title after 2 messages (user sends 1, AI responds to make 2)
      // Changed to newCount === 2 to trigger after AI response
      if (newCount === 2 && currentSession && currentSession.title === "New Chat") {
        // Add a delay to ensure assistant message is saved to database
        setTimeout(() => {
          generateTitle(currentSession.session_id).catch(err => {
            console.error('[App] Failed to generate title after message:', err);
          });
        }, 2000); // Increased to 2 seconds to ensure AI response is saved
      }
      return newCount;
    });
  }, [currentSession, generateTitle]);

  const handleSessionSwitch = async (sessionId: string) => {
    // Clear current state
    setUploadedFiles([]);
    setCurrentScript(null);
    setCurrentBreakdown(null);
    setIsAdaptedBreakdown(false);
    setExecutionResult(null);
    setMessageCount(0);  // Reset message count for new session
    setHasMessages(false);  // Reset to check messages after loading
    
    // Load new session
    await loadSession(sessionId);
    
    // Load latest script execution if exists
    try {
      const execResponse = await sessionApi.getLatestScriptExecution(sessionId);
      if (execResponse.data.execution) {
        const exec = execResponse.data.execution;
        setExecutionResult({
          script_id: exec.script_id,
          success: exec.success,
          output: exec.output || '',
          error: exec.error || undefined,
          result_file_id: exec.result_file_id || undefined,
          output_files: exec.output_files || [],
          output_file_details: exec.output_file_details || [],
          execution_time: exec.execution_time,
          executed_at: exec.completed_at || exec.started_at
        });
        
        // Auto-open Results tab if execution exists
        const session = await sessionApi.get(sessionId);
        if (session.data.mode === 'workflow') {
          setWorkflowSidebarOpen(true);
          setActiveWorkflowTab('results');
        }
      }
    } catch (error) {
      console.error('Failed to load execution results:', error);
    }
  };

  const handleNewChat = async () => {
    // Clear current state
    setUploadedFiles([]);
    setCurrentScript(null);
    setCurrentBreakdown(null);
    setIsAdaptedBreakdown(false);
    setExecutionResult(null);
    setMessageCount(0);  // Reset message count for new session
    setHasMessages(false);  // Reset to show landing page
    
    // Create new session
    await createNewSession();
  };

  const handleFilesChanged = (files: FileUploadType[]) => {
    // Auto-open workflow sidebar when files are uploaded - only in workflow mode
    if (files.length > 0 && currentSession?.mode === 'workflow') {
      setWorkflowSidebarOpen(true);
      setActiveWorkflowTab('files');
    }
    
    // Set the files after ensuring sidebar is open
    setUploadedFiles(files);
    // For chat/research modes, files are handled directly in the Chat component
  };

  const handleScriptGenerated = (script: Script, breakdown?: ScriptBreakdown) => {
    setCurrentScript(script);
    setCurrentBreakdown(breakdown || null);
    setIsAdaptedBreakdown(false); // Regular script generation is not adapted
    // Auto-switch to script tab when script is generated
    setWorkflowSidebarOpen(true);
    setActiveWorkflowTab('script');
  };

  const handleScriptExecuted = (result: ExecutionResult) => {
    setExecutionResult(result);
    // Auto-switch to results tab when execution completes
    setWorkflowSidebarOpen(true);
    setActiveWorkflowTab('results');
  };

  const handleTitleUpdate = useCallback(async (newTitle: string) => {
    if (currentSession) {
      try {
        await updateSessionTitle(currentSession.session_id, newTitle);
      } catch (error) {
        console.error('Failed to update title:', error);
      }
    }
  }, [currentSession, updateSessionTitle]);

  const handleSendMessage = async (message: string, attachedFiles?: any[]) => {
    setPendingMessage(message);
    
    // For non-workflow modes, set uploaded files if provided
    if (currentSession?.mode !== 'workflow' && attachedFiles && attachedFiles.length > 0) {
      setUploadedFiles(attachedFiles);
    }
    
    setHasMessages(true);
  };

  // Check if session has messages on load and load associated workflow data
  useEffect(() => {
    if (currentSession) {
      // Check if session has messages
      if (currentSession.messages && currentSession.messages.length > 0) {
        setHasMessages(true);
        
        // Check if this session needs a title generated
        // Generate title if: title is still "New Chat" and has at least 2 messages
        if (currentSession.title === "New Chat" && currentSession.messages.length >= 2) {
          console.log(`[App] Session ${currentSession.session_id} has ${currentSession.messages.length} messages but no title, generating...`);
          // Delay slightly to avoid overwhelming the API
          setTimeout(() => {
            generateTitle(currentSession.session_id).catch(err => {
              console.error('[App] Failed to generate title:', err);
            });
          }, 500);
        }
        
        // Look for script breakdown and workflow context in messages
        for (let i = currentSession.messages.length - 1; i >= 0; i--) {
          const message = currentSession.messages[i];
          if (message.role === 'assistant') {
            // Extract workflow context ID if present
            if (message.metadata?.context_id && !workflowContextId) {
              setWorkflowContextId(message.metadata.context_id);
              console.log('Restored workflow context ID from message history:', message.metadata.context_id);
            }
            // Extract phase if present
            if (message.metadata?.phase && typeof message.metadata.phase === 'number') {
              setCurrentPhase(message.metadata.phase);
            }
            // Extract breakdown if present
            if (message.metadata?.breakdown) {
              setCurrentBreakdown(message.metadata.breakdown);
              setIsAdaptedBreakdown(false); // Breakdown from chat history is original
            }
          }
        }
      }
      
      // Restore workflow context ID from session if available
      if (currentSession.workflow_context_id && !workflowContextId) {
        console.log('[App] Restoring workflow context from session:', currentSession.workflow_context_id);
        setWorkflowContextId(currentSession.workflow_context_id);
      }
      
      // Auto-open workflow sidebar if we have a workflow context
      if ((workflowContextId || currentSession.workflow_context_id) && currentSession?.mode === 'workflow') {
        setWorkflowSidebarOpen(true);
      }
      
      // Load files for all session modes (not just workflow)
      const loadSessionData = async () => {
        try {
          // Load files for the session - we need to fetch each file by ID
          if (currentSession.uploaded_files && currentSession.uploaded_files.length > 0) {
            const filePromises = currentSession.uploaded_files.map(fileId => 
              fileApi.getInfo(fileId).catch(err => {
                console.warn(`Failed to load file ${fileId}:`, err);
                return null;
              })
            );
            const fileResponses = await Promise.all(filePromises);
            const files = fileResponses
              .filter(response => response !== null)
              .map(response => response!.data);
            if (files.length > 0) {
              setUploadedFiles(files);
            }
          }
          
          // Load workflow-specific data
          if (currentSession.mode === 'workflow') {
            
            // First check for adapted executions
            try {
              const latestExecutionResponse = await sessionApi.getLatestExecution(currentSession.session_id);
              if (latestExecutionResponse.data.execution && latestExecutionResponse.data.execution.adapted_script_code) {
                // Found an adapted execution - load it
                const execution = latestExecutionResponse.data.execution;
                // Found adapted execution for session
                
                // Set pending execution context to trigger ExecutionLoader
                setPendingExecutionContext({
                  executionId: execution.id,
                  sessionId: currentSession.session_id
                });
                
                // Open workflow sidebar to show results
                setWorkflowSidebarOpen(true);
                setActiveWorkflowTab('results');
                return; // Skip loading regular scripts since we have an adapted execution
              }
            } catch (err) {
              // No adapted execution found, checking for regular scripts
            }
            
            // If no adapted execution, load regular scripts for the session
            const scriptsResponse = await scriptApi.listBySession(currentSession.session_id);
            if (scriptsResponse.data && scriptsResponse.data.length > 0) {
              // Get the most recent script
              const latestScript = scriptsResponse.data[0];
              setCurrentScript(latestScript);
              
              // Set breakdown if available from the script
              if (latestScript.breakdown) {
                setCurrentBreakdown(latestScript.breakdown);
                setIsAdaptedBreakdown(false);
              }
              
              // Try to get the latest execution result
              try {
                const executionResult = await scriptApi.getResults(latestScript.script_id);
                setExecutionResult(executionResult.data);
              } catch (err) {
                // No execution result yet, that's okay
                // No execution result for script yet
              }
            }
          } // End of workflow mode check
        } catch (error) {
          console.error('Failed to load session data:', error);
        }
      };
      
      loadSessionData();
    }
  }, [currentSession, generateTitle, workflowContextId]);

  // Check for style guide or test query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('styleguide') === 'true') {
      setShowStyleGuide(true);
    }
    if (params.get('teststreaming') === 'true') {
      setShowStreamingTest(true);
    }
  }, []);

  // Check for pending workflow execution on initial load
  useEffect(() => {
    const checkPendingExecution = () => {
      const pendingExecution = localStorage.getItem('pendingWorkflowExecution');
      if (pendingExecution) {
        try {
          const context = JSON.parse(pendingExecution);
          // Found pending execution on page load
          
          // First load the session
          handleSessionSwitch(context.sessionId).then(async () => {
            // Update session mode to workflow
            await updateSessionMode(context.sessionId, 'workflow');
            
            // Small delay to ensure state updates propagate
            setTimeout(() => {
              // Set the execution context to be loaded
              setPendingExecutionContext({
                executionId: context.executionId,
                sessionId: context.sessionId
              });
              
              // Set workflow mode and open sidebar
              setWorkflowSidebarOpen(true);
              setActiveWorkflowTab('results');
              setHasMessages(true); // Show the chat interface
            }, 100);
          });
          
          localStorage.removeItem('pendingWorkflowExecution');
        } catch (error) {
          console.error('Failed to parse pending execution:', error);
        }
      }
    };
    
    checkPendingExecution();
  }, []); // Run only once on mount

  if (showStyleGuide) {
    return <StyleGuide />;
  }
  
  if (showStreamingTest) {
    return <TestStreamingIssue />;
  }

  if (!currentSession) {
    return (
      <div className="loading-container">
        <LoadingSpinner message="Initializing Assertion..." size="large" />
      </div>
    );
  }

  const handleNavItemClick = async (item: string, options?: { sessionId?: string, openWorkflowSidebar?: boolean, workflowData?: any }) => {
    if (item === 'launchpad') {
      if (options?.sessionId) {
        // Load specific session instead of creating new
        await handleSessionSwitch(options.sessionId);
        // Update session mode to workflow after loading
        await updateSessionMode(options.sessionId, 'workflow');
        
        if (options.openWorkflowSidebar) {
          // Check for pending workflow execution in localStorage
          const pendingExecution = localStorage.getItem('pendingWorkflowExecution');
          if (pendingExecution) {
            try {
              const executionContext = JSON.parse(pendingExecution);
              // Found pending workflow execution
              // Set the execution context to be loaded
              setPendingExecutionContext({
                executionId: executionContext.executionId,
                sessionId: executionContext.sessionId
              });
              localStorage.removeItem('pendingWorkflowExecution');
            } catch (error) {
              console.error('Failed to parse pending execution context:', error);
            }
          }
          
          // Set a small delay to ensure session is fully loaded
          setTimeout(() => {
            setWorkflowSidebarOpen(true);
            setActiveWorkflowTab('results');
            setHasMessages(true);
          }, 100);
        }
      } else {
        // When clicking launchpad normally, create a new chat
        await handleNewChat();
      }
      setCurrentPage('launchpad');
    } else if (item === 'workflow-library') {
      setCurrentPage('workflow-library');
    } else if (item === 'column-mappings') {
      setCurrentPage('column-mappings');
    } else if (item === 'knowledge-base') {
      setCurrentPage('knowledge-base');
    }
  };

  return (
    <AppLayout activeNavItem={currentPage} onNavItemClick={handleNavItemClick}>
      {currentPage === 'launchpad' ? (
        <div className="App">
          <ChatSidebarWrapper
            currentSessionId={currentSession.session_id}
            onSessionSelect={handleSessionSwitch}
            onNewChat={handleNewChat}
          />
          
          <main className="main-content">
            {!hasMessages ? (
              <CenteredChatInput
                onSendMessage={handleSendMessage}
                sessionId={currentSession.session_id}
                onDraftCapture={(draft) => setPendingMessage(draft)}
                onFilesUploaded={(files) => {
                  handleFilesChanged(files);
                  // Auto-transition to chat view after file upload
                  setHasMessages(true);
                }}
              />
            ) : (
              <ErrorBoundary componentName="Chat">
                <Chat
                  session={currentSession}
                  uploadedFiles={uploadedFiles}
                  currentScript={currentScript}
                  onScriptGenerated={handleScriptGenerated}
                  onMessageSent={handleMessageSent}
                  onTitleUpdate={handleTitleUpdate}
                  initialMessage={pendingMessage}
                  onInitialMessageSent={() => setPendingMessage(null)}
                  onFilesChanged={handleFilesChanged}
                  workflowSidebarRef={workflowSidebarRef}
                  onWorkflowContextChange={setWorkflowContextId}
                  onPhaseChange={setCurrentPhase}
                />
              </ErrorBoundary>
            )}
          </main>
          
          {currentSession?.mode === 'workflow' && currentSession && (
            <>
              {pendingExecutionContext && (
                <ExecutionLoader
                  executionId={pendingExecutionContext.executionId}
                  sessionId={pendingExecutionContext.sessionId}
                  onExecutionLoaded={(script, result, breakdown, inputFiles, isAdaptedBreakdown) => {
                    // ExecutionLoader completed
                    if (script) {
                      setCurrentScript(script);
                    }
                    if (result) {
                      setExecutionResult(result);
                    }
                    if (breakdown) {
                      setCurrentBreakdown(breakdown);
                      // Set whether this is an adapted breakdown
                      setIsAdaptedBreakdown(isAdaptedBreakdown || false);
                    }
                    if (inputFiles && inputFiles.length > 0) {
                      // Files are already in FileUpload format from ExecutionLoader
                      setUploadedFiles(inputFiles);
                    }
                    setPendingExecutionContext(null);
                    // Force sidebar to show results tab
                    setActiveWorkflowTab('results');
                  }}
                  onError={(error) => {
                    console.error('Failed to load execution:', error);
                    setPendingExecutionContext(null);
                  }}
                />
              )}
              <WorkflowSidebar
                ref={workflowSidebarRef}
                isOpen={workflowSidebarOpen}
                onClose={() => setWorkflowSidebarOpen(false)}
                activeTab={activeWorkflowTab}
                onTabChange={(tab) => {
                  setActiveWorkflowTab(tab);
                  setWorkflowSidebarOpen(true);
                }}
                sessionId={currentSession.session_id}
                uploadedFiles={uploadedFiles}
                currentScript={currentScript}
                scriptBreakdown={currentBreakdown}
                isAdaptedBreakdown={isAdaptedBreakdown}
                executionResult={executionResult}
                workflowContextId={workflowContextId}
                currentPhase={currentPhase}
                onFilesChanged={handleFilesChanged}
                onScriptExecuted={handleScriptExecuted}
                onBreakdownUpdated={(breakdown) => {
                  console.log('App.tsx onBreakdownUpdated called with breakdown:', breakdown ? 'Yes' : 'No');
                  setCurrentBreakdown(breakdown);
                }}
                onWorkflowRestart={() => {
                  // Reset phase and clear script when columns are edited
                  setCurrentPhase(2);
                  setCurrentScript(null);
                  setCurrentBreakdown(null);
                  setExecutionResult(null);
                }}
                onScriptChange={(script) => {
                  // When switching script versions, update both script and breakdown
                  setCurrentScript(script);
                  if (script.breakdown) {
                    setCurrentBreakdown(script.breakdown);
                    setIsAdaptedBreakdown(false);
                  } else {
                    setCurrentBreakdown(null);
                  }
                }}
                onFixError={(errorMessage) => {
                  // Get script and file context for better error fixing
                  const fileNames = uploadedFiles.map(f => f.filename).join(', ');
                  const scriptInfo = currentScript ? `Script ID: ${currentScript.script_id}` : '';
                  
                  const formattedMessage = `My script execution failed and needs to be fixed.

**Error Details:**
\`\`\`
${errorMessage}
\`\`\`

**Context:**
- Files being processed: ${fileNames || 'No files'}
- ${scriptInfo}
- Workflow Phase: ${currentPhase}

Please analyze the error and provide an updated script that:
1. Fixes the specific error mentioned above
2. Handles edge cases that might cause similar errors
3. Includes proper error handling to prevent future failures

**Important**: If the error is due to data quality issues (missing columns, unexpected data formats, inconsistent data) that require clarification, please:
- Identify the specific data quality issues
- Ask me clarifying questions about how to handle them
- Wait for my confirmation before generating the fixed script

The script should maintain all the original functionality while addressing this error.`;
                  
                  setPendingMessage(formattedMessage);
                  // Close sidebar to focus on chat
                  setWorkflowSidebarOpen(false);
                }}
              />
            </>
          )}
        </div>
      ) : currentPage === 'workflow-library' ? (
        <WorkflowLibrary onNavigate={handleNavItemClick} />
      ) : currentPage === 'column-mappings' ? (
        <ColumnMappingReview />
      ) : currentPage === 'knowledge-base' ? (
        <KnowledgeBase />
      ) : null}
    </AppLayout>
  );
}

// Renamed to MainApp to avoid confusion with new AppWithAuth
function MainApp() {
  return <AppContent />;
}

export default MainApp;